import React from 'react';
import { useEffect, useState } from 'react';

import { team_index_alias, format_subteam_options, format_team_options, get_subteams } from 'src/uglib_utils/teams';
import * as styles from 'src/uglib_styles/staffDirectoryHeader.module.css'

/**
 * This staff directory header component provides search and filtering capabilities for the staff directory.
 * 
 * It provides the HTML form and the client side code to filter (AKA hide) the cards according to the values provided in the form. 
 * Usage:
 * <StaffDirectoryHeader />
 */
function StaffDirectoryHeader ({search_id}) {	

    const [subteam_options, setSubteamOptions] = useState(format_subteam_options());
    const team_options = format_team_options();

	useEffect(() => {
        let directory_search = document.getElementById('directory_search');
		let team_dropdown = document.getElementById('team_dropdown');
		let subteam_dropdown = document.getElementById('subteam_dropdown');
		let reset_button = document.getElementById('reset');
        
        directory_search.onchange = update;
        directory_search.oninput = update;
        team_dropdown.onchange = update_team;
        subteam_dropdown.onchange = update;
		reset_button.onclick = reset_filters;
        
        // Don't do filters if there is an anchor
        if(window.location.hash) {
            process_anchor();
        }
        else {
            prepopulate_filters();
        }

        // Handle anchors to specific people.
        function process_anchor() {
            let anchor = window.location.hash.substring(1);
            const target = document.getElementById(anchor);

            if (target != null) {
                hide_all();
                target.classList.remove('invisible');
            }
        }

        /**
         * Checks the URL for team and subteam query parameters and sets the filters appropriately. 
         */
        function prepopulate_filters() {
            const params = (new URL(window.location)).searchParams;
            let team_param = params.get('team');
            let subteam_param = params.get('subteam');

            if (team_param) {
                const team_dropdown = document.getElementById('team_dropdown');
                const team_values = [...team_dropdown.options].map((option) => option.value);

                // Check to see if the parameter is an alias of another index.
                // This lets us separate the anchor from the actual index used in the drupal backend.
                team_param = team_index_alias(team_param);

                if(team_values.includes(team_param)) {
                    document.getElementById('team_dropdown').value = team_param;
                }
            }

            if (subteam_param) {
                const subteam_dropdown = document.getElementById('subteam_dropdown');
                const subteam_values = [...subteam_dropdown.options].map((option) => option.value);

                // Check to see if the parameter is an alias of another index.
                // This lets us separate the anchor from the actual index used in the drupal backend.
                subteam_param = team_index_alias(subteam_param);

                if (subteam_values.includes(subteam_param)) {
                    document.getElementById('subteam_dropdown').value = subteam_param;
                }
            }

            update();
        }

        function update_team() {
            update_subteam_options();
            update();
        }

        function update() {
			const team_cards = filter_team();
            const subteam_cards = filter_subteam();
            const search_cards = filter_search();

            let result = array_intersect(team_cards, subteam_cards);
            result = array_intersect(result, search_cards);

            hide_all();
            result.forEach((item) => {
                item.classList.remove('invisible');
            });
        }

        /**
         * Updates the list of available subteams based on the selected team. If no team is selected, show all the possible values. 
         */
        function update_subteam_options() {
            const team_val = document.getElementById('team_dropdown').value;            
            setSubteamOptions(format_subteam_options(get_subteams(team_val)));
            update_subteam_dropdown_visibility();
            document.getElementById('subteam_dropdown').value = "";
        }

        /**
         * Toggles visibility of the subteam select, hiding it if the team that is selected has no subteam and showing it otherwise.
         */
        function update_subteam_dropdown_visibility() {
            const team_val = document.getElementById('team_dropdown').value;
            const options = format_subteam_options(get_subteams(team_val));

            const subteam_select = document.getElementById('subteam_dropdown');

            if(team_val === '') {
                subteam_select.parentElement.classList.remove('hidden');
                return;
            }

            if (options.length === 0) {
                subteam_select.parentElement.classList.add('hidden');
            }
            else {
                subteam_select.parentElement.classList.remove('hidden');
            }
        }
		
		/**
		 * Filter to cards that meet the criteria
		 */
		function filter_team() {
            const team_val = document.getElementById('team_dropdown').value;

            if(team_val === '') {
                return get_all_cards();
            }

            return get_team_nodes(team_val);
            
		}

        function get_all_cards() {
            return document.querySelectorAll('.card');
        }

        function get_visible_cards() {
            return document.querySelectorAll('.card:not(.hidden)')
        }

        function filter_subteam() {
            const subteam_val = document.getElementById('subteam_dropdown').value;

            if(subteam_val === '') {
                return get_all_cards();
            }

            return get_subteam_nodes(subteam_val);
        }

        /**
         * Filter cards to nodes that match the search string. 
         * @returns An array of HTML nodes
         */
        function filter_search() {
            const search_text = document.getElementById('directory_search').value;

            if(search_text === '') {
                return get_all_cards();
            }
            
            return search_nodes(search_text);
        }
		
		function array_intersect(array1, array2) {
			let results = [];
		
			for(let i=0; i < array1.length; i++) {
				for(let j=0; j < array2.length; j++) {
					if(array1[i] === array2[j] ) {
						results.push(array1[i]);
					}
				}
			}
		
			return results;
		}
		
        // Filter sections that don't match the search string
		function search_nodes(needle) {
			let cards = [...get_visible_cards()];
			needle = needle.toLowerCase();

            const result = cards.filter((element) => {
                var text = element.textContent.toLowerCase();
				return text.search(needle) !== -1;
            })

            return result;
		}

		// Get data nodes that match our team
		function get_team_nodes(team) {
			let query_string = `.card[data-team="${team}"]`;
			return document.querySelectorAll(query_string);
		}

        // Get data nodes that match our subteam
		function get_subteam_nodes(subteam) {
			let query_string = `.card[data-subteam="${subteam}"]`;
			return document.querySelectorAll(query_string);
		}
		
        // Hide all cards
		function hide_all() {
			let all_items = document.querySelectorAll('#staff_directory .card');
			all_items.forEach((item) => {
				item.classList.add('invisible');
			})
		}
		
        // Show all cards
		function show_all() {
			let items = document.querySelectorAll('#staff_directory .card');
			items.forEach((item) => {
				item.classList.remove('invisible');
			});
		}
		
		function reset_filters() {
            // Subteams maybe scoped to subteams of a particular team, so we need to reset it to all possible values.
            setSubteamOptions(format_subteam_options());

            // Unhide the subteam dropdown in case it's been hidden. 
            const subteam_select = document.getElementById('subteam_dropdown');
            subteam_select.parentElement.classList.remove('hidden');

            // Reset inputs
			document.getElementById('team_dropdown').value = "";
            document.getElementById('subteam_dropdown').value = "";
            document.getElementById('directory_search').value = "";
		
			show_all();
		}
	}, []);

	return (<>
		<div id={styles.staff_directory_header}>
            <div className={styles.form} id="staff_directory_form">
                <label for="directory_search">
                    <span className="sr-only">Search</span>
                    <input placeholder="Search" id="directory_search" type="text" aria-controls="staff_list" />
                </label>
                
                <label for="team_dropdown">
                    <span>Team</span>
                    <select className="form-select form-select-lg" name="team_dropdown" id="team_dropdown" aria-controls="staff_list">
                        <option value="">All teams</option>
                        {team_options}
                    </select>
                </label>

                <label for="subteam_dropdown">
                    <span>Subteam</span>
                    <select className="form-select form-select-lg" name="subteam_dropdown" id="subteam_dropdown" aria-controls="staff_list">
                        <option value="">All subteams</option>
                        {subteam_options}
                    </select>
                </label>
                <button type="button" className="btn btn-secondary" id="reset" aria-controls="staff_list">Reset</button>
            </div>
        </div>
        
	</>)
}

export default StaffDirectoryHeader